import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import IconButton from "@material-ui/core/IconButton";
import { InputBaseProps } from "@material-ui/core/InputBase";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import Loader from "components/Loader";
import TextInputBase from "components/TextInputBase";
import { MuiTheme } from "theme";
import {
  MIN_CHARS_NUMBER_FOR_SEARCH,
  TSupportedEntity
} from "utils/app-search-utils";
import { getRequiredValue } from "utils/required-values-utils";
import SuggestionList from "./SuggestionList";
import { getEntityName } from "./helper";

import { useAppSearch } from "hooks/useAppSearch";
import useStyles, {
  INPUT_BOTTOM_MARGIN,
  INPUT_BOTTOM_MARGIN_XS,
  IStylesProps,
  ITEM_HEIGHT,
  ITEM_HEIGHT_DOWN_XS
} from "./styles";

interface AppSearchClassNames {
  root?: string;
  itemsList?: string;
  formContent?: string;
  clearIcon?: string;
  searchIcon?: string;
  input?: string;
}

interface Props extends InputBaseProps {
  placeholder?: string;
  value?: string;
  isForceSearch?: boolean;
  className?: string;
  classNames?: AppSearchClassNames;
  shouldMakeHorseOrTrainerRequest?: boolean;
  clearSearch?: boolean;
  isRedirect?: boolean;
  showFullList?: boolean;
  showListOpened?: boolean;
  isCloseOnClickAway?: boolean;
  activeHorse?: racehorse360.IHorse | null;
  showClearSearchButton?: boolean;
  withCheckMarkButton?: boolean;
  workoutRequestsFilter?: racehorse360.IWorkoutRequestFilter;
  upcomingRaceEntriesFilter?: racehorse360.IUpcomingRaceEntryFilter;
  stallApplicationsFilter?: racehorse360.IStallApplicationFilter;
  onClearSearch?: (value: boolean) => void;
  onCloseDialog?: () => void;
  onForceSearch?: (
    value: string,
    item: racehorse360.ITrainer | racehorse360.IHorse | null
  ) => void;
  onItemClick?: (item: TSupportedEntity) => void;
  onRedirect?: (horseId: string) => void;
  onSearch?: (value: string, item?: TSupportedEntity) => void;
}

const AppSearch = (props: Props) => {
  const {
    placeholder,
    className,
    isForceSearch,
    classNames,
    isRedirect = false,
    showFullList = false,
    showListOpened = false,
    isCloseOnClickAway = true,
    activeHorse = null,
    showClearSearchButton = false,
    workoutRequestsFilter,
    upcomingRaceEntriesFilter,
    stallApplicationsFilter,
    shouldMakeHorseOrTrainerRequest = false,
    clearSearch,
    withCheckMarkButton = false,
    onBlur,
    onClearSearch,
    onCloseDialog,
    onFocus,
    onItemClick,
    onSearch,
    onRedirect
  } = props;

  const theme = useTheme<MuiTheme>();
  const formRef = useRef<HTMLFormElement>();
  const inputRef = useRef<HTMLInputElement>();
  const infiniteScrollRootRef = useRef<HTMLUListElement>(null);
  const listContainerRef = useRef(null);
  const matchesDownXs = useMediaQuery(theme.breakpoints.down("xs"), {
    noSsr: true
  });

  const [stylesProps, setStylesProps] = useState<IStylesProps>();
  const classes = useStyles(stylesProps);

  const [focused, setFocused] = useState(false);
  const [isListOpened, setIsListOpened] = useState(showListOpened);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);

  const [value, setValue] = useState(
    getRequiredValue(props.value, EMPTY_STRING)
  );
  const [searchValue, setSearchValue] = useState(value);

  const itemHeight = matchesDownXs ? ITEM_HEIGHT_DOWN_XS : ITEM_HEIGHT;

  const isCloseIconFocused = [focused, value.length].some(Boolean);
  const shouldStartSearch = [
    focused,
    searchValue.length >= MIN_CHARS_NUMBER_FOR_SEARCH
  ].every(Boolean);
  const shouldShowCheckMarkButton = [
    shouldStartSearch,
    withCheckMarkButton
  ].every(Boolean);

  const { items, isLoading, isFetchingNextPage } = useAppSearch(
    searchValue,
    isListOpened,
    showFullList,
    shouldMakeHorseOrTrainerRequest,
    stallApplicationsFilter,
    workoutRequestsFilter,
    upcomingRaceEntriesFilter,
    infiniteScrollRootRef
  );

  if (activeHorse && items.length && !value) {
    const horseActiveIndex = items.findIndex(
      horse => horse.id === activeHorse.id
    );

    if (horseActiveIndex > -1) {
      items.splice(horseActiveIndex, 1);
    }
    items.unshift(activeHorse);
  }

  const selectItem = (item: TSupportedEntity) => {
    const name = getEntityName(item);

    setValue(name);
    setIsListOpened(false);

    if (
      item instanceof racehorse360.Horse &&
      item.name?.length >= MIN_CHARS_NUMBER_FOR_SEARCH &&
      isRedirect
    ) {
      if (activeHorse?.id === item.id) {
        //Close pop up when click on the green active horse row
        setIsListOpened(false);
        onCloseDialog();
      } else {
        onRedirect(item.id);
      }
    } else {
      applySearch(name, true, item);
    }
  };

  const applySearch = (
    value: string,
    submit?: boolean,
    item?: TSupportedEntity
  ) => {
    setSearchValue(value);

    if ((value !== searchValue && !isForceSearch) || submit) {
      onSearch && onSearch(value, item);
    }
  };

  const isItemInView = (index: number) => {
    const viewStart = listContainerRef.current.scrollTop;
    const viewEnd = viewStart + listContainerRef.current.offsetHeight;
    return (
      index * itemHeight + itemHeight * 1.5 < viewEnd &&
      index * itemHeight - itemHeight / 2 > viewStart
    );
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isListOpened) {
      switch (event.key) {
        case "Up":
        case "ArrowUp": {
          event.preventDefault();
          const nextActive =
            activeItemIndex > 0 ? activeItemIndex - 1 : items.length - 1;
          if (!isItemInView(nextActive)) {
            listContainerRef.current.scrollTop =
              nextActive * itemHeight - itemHeight / 2;
          }
          setActiveItemIndex(nextActive);
          break;
        }
        case "Down":
        case "ArrowDown": {
          event.preventDefault();
          let nextActive = 0;
          if (activeItemIndex !== null) {
            nextActive =
              activeItemIndex < items.length - 1 ? activeItemIndex + 1 : 0;
          }
          if (!isItemInView(nextActive)) {
            listContainerRef.current.scrollTop =
              nextActive * itemHeight -
              listContainerRef.current.offsetHeight +
              itemHeight * 1.5;
          }
          setActiveItemIndex(nextActive);
          break;
        }
      }
    }
  };

  const handleValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setValue(value);
    setActiveItemIndex(null);

    if (value.length >= MIN_CHARS_NUMBER_FOR_SEARCH) {
      setIsListOpened(true);
      applySearch(value);
    } else {
      setIsListOpened(false);
      applySearch("");
    }
  };

  const handleClear = () => {
    setValue("");
    !showListOpened && setIsListOpened(false);
    applySearch("", true);
  };

  const handleInputFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFocused(true);
    onFocus && onFocus(event);
  };

  const handleInputBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFocused(false);
    onBlur && onBlur(event);
  };

  const handleItemClick = (item: TSupportedEntity) => () => {
    selectItem(item);
    onItemClick?.(item);
  };

  const handleClickAway = () => {
    isCloseOnClickAway && setIsListOpened(false);
  };

  const handleSubmit = () => {
    if (activeItemIndex !== null) {
      selectItem(items[activeItemIndex]);
    } else if (isForceSearch) {
      applySearch(searchValue, true);
    }

    !showListOpened && setIsListOpened(false);
    setFocused(false);
  };

  useEffect(() => {
    clearSearch && handleClear();
    onClearSearch && onClearSearch(false);
  }, [clearSearch]);

  useEffect(() => {
    if (shouldStartSearch) {
      setIsListOpened(true);
    }
  }, [focused]);

  useEffect(() => {
    if (isListOpened) {
      const listContainerRelativeTop =
        formRef.current.offsetHeight +
        (matchesDownXs ? INPUT_BOTTOM_MARGIN_XS : INPUT_BOTTOM_MARGIN);
      const listContainerAbsoluteTop =
        formRef.current.getBoundingClientRect().bottom +
        (matchesDownXs ? INPUT_BOTTOM_MARGIN_XS : INPUT_BOTTOM_MARGIN);
      setStylesProps({
        listContainerRelativeTop,
        listContainerAbsoluteTop
      });
    }
  }, [isListOpened]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={clsx(classes.appSearch, className, classNames?.root)}>
        <form
          ref={formRef}
          action={"#"}
          className={clsx(classes.formContent, classNames?.formContent, {
            [classes.formFocused]: focused
          })}
          onSubmit={handleSubmit}
        >
          <SearchIcon
            className={clsx(classes.searchIcon, classNames?.searchIcon, {
              [classes.iconFocused]: focused
            })}
          />
          <TextInputBase
            className={classes.inputBase}
            classes={{
              input: clsx(classes.input, classNames?.input, {
                [classes.inputBaseFocused]: focused
              })
            }}
            placeholder={placeholder}
            type="search"
            value={value}
            onChange={handleValueChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onEnter={handleSubmit}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
          />
          {shouldShowCheckMarkButton && (
            <IconButton
              className={classes.checkMarkButton}
              onClick={handleSubmit}
            >
              <CheckIcon />
            </IconButton>
          )}
          {!!value.length && (
            <CloseIcon
              className={clsx(classes.closeIcon, classNames?.clearIcon, {
                [classes.closeIconFocused]: isCloseIconFocused
              })}
              onClick={handleClear}
            />
          )}
        </form>
        {isListOpened && (
          <div
            ref={listContainerRef}
            className={clsx(classes.items, classNames?.itemsList)}
          >
            <SuggestionList
              searchValue={searchValue}
              isLoading={isLoading}
              items={items}
              value={value}
              tableBodyRef={infiniteScrollRootRef}
              onItemClick={handleItemClick}
              onClear={handleClear}
              showClearSearchButton={showClearSearchButton}
              activeItemIndex={activeItemIndex}
              activeHorseId={activeHorse?.id}
            />
            <Loader
              size={31}
              className={clsx(classes.nextPageLoader, {
                visible: isFetchingNextPage
              })}
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default React.memo(AppSearch);
